(() => {
  'use strict';

  const $ = (root, selector) => root.querySelector(selector);
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const CCS_BUILD = '2.0.9';
  console.info(`[CCS Cut File Maker] Build ${CCS_BUILD} loaded`);

  function debounce(fn, wait = 180) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  function quantile(values, q) {
    if (!values.length) return 0;
    const sorted = values.slice().sort((a, b) => a - b);
    const index = Math.max(0, Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * q)));
    return sorted[index];
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function init(root) {
    if (root.dataset.ccsInitialised === 'true') return;
    root.dataset.ccsInitialised = 'true';

    const els = {
      upload: $(root, '[data-ccs-upload]'),
      threshold: $(root, '[data-ccs-threshold]'),
      thresholdValue: $(root, '[data-ccs-threshold-value]'),
      smoothing: $(root, '[data-ccs-smoothing]'),
      smoothingValue: $(root, '[data-ccs-smoothing-value]'),
      detail: $(root, '[data-ccs-detail]'),
      detailValue: $(root, '[data-ccs-detail-value]'),
      invert: $(root, '[data-ccs-invert]'),
      vectorColour: $(root, '[data-ccs-vector-colour]'),
      vectorColourValue: $(root, '[data-ccs-vector-colour-value]'),
      sizeBy: $(root, '[data-ccs-size-by]'),
      size: $(root, '[data-ccs-size]'),
      approval: $(root, '[data-ccs-approval]'),
      prepare: $(root, '[data-ccs-prepare]'),
      download: $(root, '[data-ccs-download]'),
      status: $(root, '[data-ccs-status]'),
      preview: $(root, '[data-ccs-preview]'),
      previewScale: $(root, '[data-ccs-preview-scale]'),
      measure: $(root, '[data-ccs-measure]'),
      fileName: $(root, '[data-ccs-file-name]'),
      complexity: $(root, '[data-ccs-complexity]'),
      minFeature: $(root, '[data-ccs-min-feature]'),
      complexityMessage: $(root, '[data-ccs-complexity-message]'),
      generated: $(root, '[data-ccs-generated]'),
      engine: $(root, '[data-ccs-engine]'),
      rasterControls: $(root, '[data-ccs-raster-controls]'),
      zoom: $(root, '[data-ccs-zoom]'),
      zoomLabel: $(root, '[data-ccs-zoom-label]'),
      zoomIn: $(root, '[data-ccs-zoom-in]'),
      zoomOut: $(root, '[data-ccs-zoom-out]'),
      zoomReset: $(root, '[data-ccs-zoom-reset]'),
      fit: $(root, '[data-ccs-fit]'),
      viewOriginal: $(root, '[data-ccs-view-original]'),
      viewConverted: $(root, '[data-ccs-view-converted]'),
      applyRecommended: $(root, '[data-ccs-apply-recommended]'),
      pieceCount: $(root, '[data-ccs-piece-count]'),
      tinyPieces: $(root, '[data-ccs-tiny-pieces]'),
      buildBadge: $(root, '[data-ccs-build-badge]'),
      buildProof: $(root, '[data-ccs-build-proof]')
    };

    const state = {
      file: null,
      sourceType: null,
      sourceImage: null,
      sourceSvg: null,
      outputSvg: '',
      aspect: 1,
      widthMm: 0,
      heightMm: 0,
      ready: false,
      form: null,
      hidden: {},
      analysis: null,
      sizeAnalysis: null,
      zoom: 1,
      viewMode: 'converted',
      originalUrl: null,
      vectorColour: '#000000'
    };

    if (els.buildBadge) els.buildBadge.textContent = `VERSION ${CCS_BUILD}`;
    if (els.buildProof) els.buildProof.textContent = `Loaded build ${CCS_BUILD}`;
    root.dataset.ccsBuild = CCS_BUILD;

    function findProductForm() {
      const productInfo = root.closest('product-info') || document.querySelector('product-info');
      return productInfo?.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    }

    function ensureOrderFields() {
      state.form = findProductForm();
      if (!state.form) return false;
      state.form.enctype = 'multipart/form-data';

      const definitions = [
        ['file', 'properties[Cut-ready SVG]', 'ccsCutFile'],
        ['hidden', 'properties[Finished dimensions]', 'ccsDimensions'],
        ['hidden', 'properties[Size set by]', 'ccsSizeBy'],
        ['hidden', 'properties[Original filename]', 'ccsOriginalName'],
        ['hidden', 'properties[Trace settings]', 'ccsTraceSettings'],
        ['hidden', 'properties[Artwork approval]', 'ccsApproval'],
        ['hidden', 'properties[Complexity summary]', 'ccsComplexity'],
        ['hidden', 'properties[Vector colour]', 'ccsVectorColour']
      ];

      definitions.forEach(([type, name, key]) => {
        let input = state.form.querySelector(`[data-${key}]`);
        if (!input) {
          input = document.createElement('input');
          input.type = type;
          input.name = name;
          input.dataset[key] = '';
          input.className = 'ccs-cut-maker__hidden';
          if (type === 'file') input.accept = '.svg,image/svg+xml';
          state.form.appendChild(input);
        }
        state.hidden[key] = input;
      });
      return true;
    }

    function setStatus(message, type = 'warn') {
      els.status.textContent = message;
      els.status.dataset.state = type;
    }

    function setReady(value) {
      state.ready = value;
      if (els.generated) els.generated.textContent = value ? 'Attached to order' : 'Not ready';
      if (!value && state.hidden.ccsCutFile) {
        try { state.hidden.ccsCutFile.value = ''; } catch (_) {}
      }
    }

    function updateDownloadAvailability() {
      els.download.disabled = !(state.outputSvg && state.widthMm > 0 && state.heightMm > 0);
    }

    function updatePreviewCanvasSize() {
      if (!els.previewScale || !els.previewScale.parentElement) return;
      const viewport = els.previewScale.parentElement;
      const availableWidth = Math.max(240, viewport.clientWidth - 44);
      const availableHeight = Math.max(240, viewport.clientHeight - 80);
      const aspect = state.aspect && Number.isFinite(state.aspect) ? state.aspect : 1;
      let fittedWidth = availableWidth;
      let fittedHeight = fittedWidth / aspect;
      if (fittedHeight > availableHeight) {
        fittedHeight = availableHeight;
        fittedWidth = fittedHeight * aspect;
      }
      els.previewScale.style.width = `${Math.max(1, fittedWidth * state.zoom)}px`;
      els.previewScale.style.height = `${Math.max(1, fittedHeight * state.zoom)}px`;
      els.previewScale.style.transform = 'none';
      const isZoomed = state.zoom > 1.001;
      viewport.classList.toggle('is-zoomed', isZoomed);

      // Keep fitted artwork visually centred. When zoomed, start from the
      // centre of the enlarged canvas while still allowing free panning.
      requestAnimationFrame(() => {
        if (!isZoomed) {
          viewport.scrollLeft = 0;
          viewport.scrollTop = 0;
          return;
        }
        viewport.scrollLeft = Math.max(0, (viewport.scrollWidth - viewport.clientWidth) / 2);
        viewport.scrollTop = Math.max(0, (viewport.scrollHeight - viewport.clientHeight) / 2);
      });
    }

    function setZoom(value) {
      state.zoom = clamp(Number(value) || 1, 1, 8);
      if (els.zoom) els.zoom.value = String(state.zoom);
      if (els.zoomLabel) els.zoomLabel.textContent = `${Math.round(state.zoom * 100)}%`;
      updatePreviewCanvasSize();
    }

    function normaliseHexColour(value) {
      const colour = String(value || '').trim();
      return /^#[0-9a-f]{6}$/i.test(colour) ? colour.toUpperCase() : '#000000';
    }

    function colouriseSvg(svgString) {
      if (!svgString) return '';
      const colour = normaliseHexColour(state.vectorColour);
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      if (doc.querySelector('parsererror')) return svgString;
      const svg = doc.documentElement;
      svg.querySelectorAll('path,rect,circle,ellipse,polygon,polyline,line').forEach(shape => {
        const fill = (shape.getAttribute('fill') || '').trim().toLowerCase();
        const stroke = (shape.getAttribute('stroke') || '').trim().toLowerCase();
        if (fill && fill !== 'none' && fill !== 'transparent') shape.setAttribute('fill', colour);
        if (stroke && stroke !== 'none' && stroke !== 'transparent') shape.setAttribute('stroke', colour);
      });
      return new XMLSerializer().serializeToString(svg);
    }

    function renderPreview() {
      if (!els.preview) return;
      if (state.viewMode === 'original' && state.originalUrl) {
        els.preview.innerHTML = `<img src="${state.originalUrl}" alt="Original uploaded artwork">`;
      } else if (state.outputSvg) {
        els.preview.innerHTML = colouriseSvg(state.outputSvg);
      }
      if (els.viewOriginal) els.viewOriginal.classList.toggle('is-active', state.viewMode === 'original');
      if (els.viewConverted) els.viewConverted.classList.toggle('is-active', state.viewMode === 'converted');
      setZoom(state.zoom);
    }

    function setViewMode(mode) {
      if (mode === 'original' && !state.originalUrl) return;
      if (mode === 'converted' && !state.outputSvg) return;
      state.viewMode = mode;
      renderPreview();
    }

    function findAlphaBounds(imageData) {
      const { width, height, data } = imageData;
      let minX = width, minY = height, maxX = -1, maxY = -1;
      for (let y = 0; y < height; y += 1) {
        for (let x = 0; x < width; x += 1) {
          if (data[(y * width + x) * 4 + 3] > 0) {
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (maxX < minX || maxY < minY) return null;
      return {
        x: minX,
        y: minY,
        width: maxX - minX + 1,
        height: maxY - minY + 1
      };
    }

    function cropImageData(imageData, bounds) {
      const canvas = document.createElement('canvas');
      canvas.width = bounds.width;
      canvas.height = bounds.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.putImageData(imageData, -bounds.x, -bounds.y);
      return ctx.getImageData(0, 0, bounds.width, bounds.height);
    }

    function analyseBinaryImageData(imageData) {
      const bounds = findAlphaBounds(imageData);
      if (!bounds) return null;
      let cropped = cropImageData(imageData, bounds);

      const maxAnalysis = 420;
      if (Math.max(cropped.width, cropped.height) > maxAnalysis) {
        const scale = maxAnalysis / Math.max(cropped.width, cropped.height);
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(cropped.width * scale));
        canvas.height = Math.max(1, Math.round(cropped.height * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const temp = document.createElement('canvas');
        temp.width = cropped.width;
        temp.height = cropped.height;
        temp.getContext('2d').putImageData(cropped, 0, 0);
        ctx.drawImage(temp, 0, 0, canvas.width, canvas.height);
        const drawn = ctx.getImageData(0, 0, canvas.width, canvas.height);
        for (let i = 0; i < drawn.data.length; i += 4) {
          const solid = drawn.data[i + 3] > 127;
          drawn.data[i] = solid ? 0 : 255;
          drawn.data[i + 1] = solid ? 0 : 255;
          drawn.data[i + 2] = solid ? 0 : 255;
          drawn.data[i + 3] = solid ? 255 : 0;
        }
        cropped = drawn;
      }

      const width = cropped.width;
      const height = cropped.height;
      const mask = new Uint8Array(width * height);
      const runX = [];
      const runY = [];
      let foreground = 0;
      let boundary = 0;

      for (let y = 0; y < height; y += 1) {
        let currentRun = 0;
        for (let x = 0; x < width; x += 1) {
          const index = y * width + x;
          const solid = cropped.data[index * 4 + 3] > 0 ? 1 : 0;
          mask[index] = solid;
          if (solid) {
            foreground += 1;
            currentRun += 1;
            const left = x > 0 ? (cropped.data[(index - 1) * 4 + 3] > 0 ? 1 : 0) : 0;
            const right = x < width - 1 ? (cropped.data[(index + 1) * 4 + 3] > 0 ? 1 : 0) : 0;
            const up = y > 0 ? (cropped.data[(index - width) * 4 + 3] > 0 ? 1 : 0) : 0;
            const down = y < height - 1 ? (cropped.data[(index + width) * 4 + 3] > 0 ? 1 : 0) : 0;
            if (!(left && right && up && down)) boundary += 1;
          } else if (currentRun) {
            runX.push(currentRun);
            currentRun = 0;
          }
        }
        if (currentRun) runX.push(currentRun);
      }

      for (let x = 0; x < width; x += 1) {
        let currentRun = 0;
        for (let y = 0; y < height; y += 1) {
          const solid = mask[y * width + x];
          if (solid) currentRun += 1;
          else if (currentRun) {
            runY.push(currentRun);
            currentRun = 0;
          }
        }
        if (currentRun) runY.push(currentRun);
      }

      const visited = new Uint8Array(width * height);
      const components = [];
      const queue = new Int32Array(width * height);
      const neighbourOffsets = [-1, 1, -width, width, -width - 1, -width + 1, width - 1, width + 1];

      for (let i = 0; i < mask.length; i += 1) {
        if (!mask[i] || visited[i]) continue;
        let head = 0;
        let tail = 0;
        queue[tail++] = i;
        visited[i] = 1;
        let area = 0;
        while (head < tail) {
          const current = queue[head++];
          area += 1;
          const x = current % width;
          const y = Math.floor(current / width);
          for (let n = 0; n < neighbourOffsets.length; n += 1) {
            const next = current + neighbourOffsets[n];
            if (next < 0 || next >= mask.length || visited[next] || !mask[next]) continue;
            const nx = next % width;
            const ny = Math.floor(next / width);
            if (Math.abs(nx - x) > 1 || Math.abs(ny - y) > 1) continue;
            visited[next] = 1;
            queue[tail++] = next;
          }
        }
        components.push(area);
      }

      const roughness = foreground ? boundary / foreground : 0;
      return {
        width,
        height,
        foreground,
        boundary,
        roughness,
        components: components.length,
        componentAreas: components,
        minRunX: quantile(runX, 0.08) || 1,
        minRunY: quantile(runY, 0.08) || 1,
        fillRatio: foreground / (width * height)
      };
    }

    async function rasteriseSvgForAnalysis(svgString) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;
      const viewBox = (svg.getAttribute('viewBox') || '').split(/\s+/).map(Number).filter(n => Number.isFinite(n));
      const vbWidth = viewBox[2] || 100;
      const vbHeight = viewBox[3] || 100;
      const scale = 420 / Math.max(vbWidth, vbHeight);
      const width = Math.max(1, Math.round(vbWidth * scale));
      const height = Math.max(1, Math.round(vbHeight * scale));
      svg.setAttribute('width', String(width));
      svg.setAttribute('height', String(height));
      svg.setAttribute('xmlns', SVG_NS);
      const serialised = new XMLSerializer().serializeToString(svg);
      const blob = new Blob([serialised], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      try {
        const img = await new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () => reject(new Error('SVG analysis could not be completed.'));
          image.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const imageData = ctx.getImageData(0, 0, width, height);
        for (let i = 0; i < imageData.data.length; i += 4) {
          const solid = imageData.data[i + 3] > 20;
          imageData.data[i] = solid ? 0 : 255;
          imageData.data[i + 1] = solid ? 0 : 255;
          imageData.data[i + 2] = solid ? 0 : 255;
          imageData.data[i + 3] = solid ? 255 : 0;
        }
        return imageData;
      } finally {
        URL.revokeObjectURL(url);
      }
    }

    function getSizeAnalysis() {
      if (!state.analysis || !state.widthMm || !state.heightMm) return null;
      const mmPerPxX = state.widthMm / state.analysis.width;
      const mmPerPxY = state.heightMm / state.analysis.height;
      const minFeatureMm = Math.min(state.analysis.minRunX * mmPerPxX, state.analysis.minRunY * mmPerPxY);
      const smallIslandAreaThreshold = 4; // mm²
      const tinyIslands = state.analysis.componentAreas.filter(areaPx => (areaPx * mmPerPxX * mmPerPxY) < smallIslandAreaThreshold).length;
      const score = Math.round(clamp(
        (state.analysis.components * 0.45) + (tinyIslands * 1.2) + (state.analysis.roughness * 35),
        1,
        100
      ));
      const targetMinFeature = Number(root.dataset.minFeature || 1.2);
      const blocked = minFeatureMm < targetMinFeature;
      const recommendedScale = blocked ? (targetMinFeature / Math.max(minFeatureMm, 0.01)) : 1;
      const recommendedWidth = Math.ceil(state.widthMm * recommendedScale);
      const recommendedHeight = Math.ceil(state.heightMm * recommendedScale);
      let label = 'Low';
      if (score >= 60) label = 'High';
      else if (score >= 30) label = 'Medium';
      return {
        minFeatureMm,
        tinyIslands,
        score,
        label,
        components: state.analysis.components,
        blocked,
        recommendedWidth,
        recommendedHeight,
        message: blocked
          ? `This design is too detailed at the selected size. Small features are approximately ${minFeatureMm.toFixed(2)} mm. Increase to at least ${recommendedWidth} × ${recommendedHeight} mm.`
          : `Complexity ${label}. Approx. minimum feature ${minFeatureMm.toFixed(2)} mm across ${state.analysis.components} separate shapes.`
      };
    }

    function updateComplexityUi() {
      state.sizeAnalysis = getSizeAnalysis();
      if (!state.sizeAnalysis) {
        els.complexity.textContent = '—';
        els.minFeature.textContent = '—';
        if (els.pieceCount) els.pieceCount.textContent = '—';
        if (els.tinyPieces) els.tinyPieces.textContent = '—';
        if (els.applyRecommended) els.applyRecommended.classList.add('ccs-cut-maker__hidden');
        els.complexityMessage.textContent = 'Complexity check will appear after artwork is generated.';
        els.complexityMessage.dataset.state = 'warn';
        return;
      }
      els.complexity.textContent = `${state.sizeAnalysis.label} (${state.sizeAnalysis.score}/100)`;
      els.minFeature.textContent = `${state.sizeAnalysis.minFeatureMm.toFixed(2)} mm`;
      if (els.pieceCount) els.pieceCount.textContent = String(state.sizeAnalysis.components);
      if (els.tinyPieces) els.tinyPieces.textContent = String(state.sizeAnalysis.tinyIslands);
      if (els.applyRecommended) {
        els.applyRecommended.textContent = `Apply recommended size: ${state.sizeAnalysis.recommendedWidth} × ${state.sizeAnalysis.recommendedHeight} mm`;
        els.applyRecommended.classList.toggle('ccs-cut-maker__hidden', !state.sizeAnalysis.blocked);
      }
      const extraWarning = state.sizeAnalysis.tinyIslands > 0 ? ` ${state.sizeAnalysis.tinyIslands} tiny loose piece${state.sizeAnalysis.tinyIslands === 1 ? '' : 's'} may be difficult to weed or apply.` : '';
      els.complexityMessage.textContent = state.sizeAnalysis.message + extraWarning;
      els.complexityMessage.dataset.state = state.sizeAnalysis.blocked ? 'error' : (state.sizeAnalysis.tinyIslands > 0 ? 'warn' : 'ready');
    }

    function updateDimensions() {
      if (!state.aspect || !Number.isFinite(state.aspect)) return;
      const requested = Math.max(0, Number(els.size.value || 0));
      if (els.sizeBy.value === 'height') {
        state.heightMm = requested;
        state.widthMm = requested * state.aspect;
      } else {
        state.widthMm = requested;
        state.heightMm = requested / state.aspect;
      }
      const width = Math.round(state.widthMm * 10) / 10;
      const height = Math.round(state.heightMm * 10) / 10;
      els.measure.textContent = `Finished size: ${width} × ${height} mm`;
      if (state.hidden.ccsDimensions) state.hidden.ccsDimensions.value = `${width} × ${height} mm`;
      if (state.hidden.ccsSizeBy) state.hidden.ccsSizeBy.value = els.sizeBy.value;
      setReady(false);
      updateComplexityUi();
      updateDownloadAvailability();
      validate();
    }

    function validate() {
      const min = Number(root.dataset.minSize || 40);
      const max = Number(root.dataset.maxSize || 1000);
      const smaller = Math.min(state.widthMm, state.heightMm);
      const larger = Math.max(state.widthMm, state.heightMm);
      let message = 'Adjust the artwork and approve it before preparing the cut file.';
      let type = 'warn';
      const prepareEnabled = root.dataset.showPrepare === 'true';
      let valid = Boolean(state.outputSvg && (!prepareEnabled || els.approval?.checked));

      if (!state.file) {
        valid = false;
        message = 'Upload artwork to begin.';
      } else if (!state.outputSvg) {
        valid = false;
        message = 'The artwork has not produced a usable cut shape.';
        type = 'error';
      } else if (smaller <= 0 || larger <= 0) {
        valid = false;
        message = 'Enter a valid finished size.';
        type = 'error';
      } else if (larger < min || larger > max) {
        valid = false;
        message = `Choose a size between ${min} mm and ${max} mm.`;
        type = 'error';
      } else if (state.sizeAnalysis?.blocked) {
        valid = false;
        message = state.sizeAnalysis.message;
        type = 'error';
      } else if (prepareEnabled && !els.approval?.checked) {
        message = 'Check the artwork approval box to continue.';
      } else {
        message = state.ready ? 'Cut-ready SVG attached. You can add this product to the basket.' : 'Artwork is ready. Select “Prepare cut file”.';
        type = 'ready';
      }

      if (els.prepare) els.prepare.disabled = !valid || state.ready;
      setStatus(message, type);
      updateDownloadAvailability();
      return valid;
    }

    function loadImageFile(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Image could not be loaded.'));
        };
        img.src = url;
      });
    }

    async function loadSvgFile(file) {
      const text = await file.text();
      const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
      if (doc.querySelector('parsererror')) throw new Error('This SVG could not be read.');
      const source = doc.documentElement;
      source.querySelectorAll('script,foreignObject,iframe,object,embed,image,audio,video').forEach(el => el.remove());
      source.querySelectorAll('*').forEach(el => {
        [...el.attributes].forEach(attr => {
          const name = attr.name.toLowerCase();
          const value = attr.value.trim().toLowerCase();
          if (name.startsWith('on') || value.startsWith('javascript:')) el.removeAttribute(attr.name);
        });
      });
      return new XMLSerializer().serializeToString(source);
    }



    function pointLineDistance(point, start, end) {
      const dx = end.x - start.x;
      const dy = end.y - start.y;
      if (!dx && !dy) return Math.hypot(point.x - start.x, point.y - start.y);
      const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / ((dx * dx) + (dy * dy));
      const clamped = Math.max(0, Math.min(1, t));
      const px = start.x + (dx * clamped);
      const py = start.y + (dy * clamped);
      return Math.hypot(point.x - px, point.y - py);
    }

    function simplifyRdp(points, epsilon) {
      if (points.length <= 2) return points.slice();
      let maxDistance = 0;
      let index = -1;
      const first = points[0];
      const last = points[points.length - 1];
      for (let i = 1; i < points.length - 1; i += 1) {
        const distance = pointLineDistance(points[i], first, last);
        if (distance > maxDistance) {
          maxDistance = distance;
          index = i;
        }
      }
      if (maxDistance > epsilon && index > -1) {
        const left = simplifyRdp(points.slice(0, index + 1), epsilon);
        const right = simplifyRdp(points.slice(index), epsilon);
        return left.slice(0, -1).concat(right);
      }
      return [first, last];
    }

    function tokenisePathData(d) {
      return (d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[-+]?\d+)?/g) || []);
    }

    function parseSimpleLinePath(d) {
      const tokens = tokenisePathData(d);
      if (!tokens.length) return null;
      let i = 0;
      let command = null;
      let currentX = 0;
      let currentY = 0;
      let startX = 0;
      let startY = 0;
      const subpaths = [];
      let current = null;

      function ensurePath() {
        if (!current) {
          current = { points: [], closed: false };
          subpaths.push(current);
        }
      }

      while (i < tokens.length) {
        if (/[a-zA-Z]/.test(tokens[i])) command = tokens[i++];
        if (!command) return null;
        const lower = command.toLowerCase();
        const relative = command === lower;
        if (!'mlhvzMLHVZ'.includes(command)) return null;

        if (lower === 'z') {
          if (current) current.closed = true;
          currentX = startX;
          currentY = startY;
          current = null;
          continue;
        }

        if (lower === 'm') {
          if (i + 1 >= tokens.length) return null;
          let x = parseFloat(tokens[i++]);
          let y = parseFloat(tokens[i++]);
          if (relative) { x += currentX; y += currentY; }
          currentX = x; currentY = y; startX = x; startY = y;
          current = { points: [{ x, y }], closed: false };
          subpaths.push(current);
          command = relative ? 'l' : 'L';
          continue;
        }

        ensurePath();
        if (lower === 'l') {
          while (i + 1 < tokens.length && !/[a-zA-Z]/.test(tokens[i])) {
            let x = parseFloat(tokens[i++]);
            let y = parseFloat(tokens[i++]);
            if (relative) { x += currentX; y += currentY; }
            currentX = x; currentY = y;
            current.points.push({ x, y });
          }
        } else if (lower === 'h') {
          while (i < tokens.length && !/[a-zA-Z]/.test(tokens[i])) {
            let x = parseFloat(tokens[i++]);
            if (relative) x += currentX;
            currentX = x;
            current.points.push({ x, y: currentY });
          }
        } else if (lower === 'v') {
          while (i < tokens.length && !/[a-zA-Z]/.test(tokens[i])) {
            let y = parseFloat(tokens[i++]);
            if (relative) y += currentY;
            currentY = y;
            current.points.push({ x: currentX, y });
          }
        }
      }
      return subpaths;
    }

    function serialiseSimpleLinePath(subpaths) {
      return subpaths.map((subpath) => {
        if (!subpath.points.length) return '';
        let d = `M ${subpath.points[0].x.toFixed(2)} ${subpath.points[0].y.toFixed(2)}`;
        for (let i = 1; i < subpath.points.length; i += 1) {
          d += ` L ${subpath.points[i].x.toFixed(2)} ${subpath.points[i].y.toFixed(2)}`;
        }
        if (subpath.closed) d += ' Z';
        return d;
      }).filter(Boolean).join(' ');
    }

    function getTurnAngleDegrees(prev, current, next) {
      const ax = current.x - prev.x;
      const ay = current.y - prev.y;
      const bx = next.x - current.x;
      const by = next.y - current.y;
      const al = Math.hypot(ax, ay);
      const bl = Math.hypot(bx, by);
      if (!al || !bl) return 0;
      const cos = clamp(((ax * bx) + (ay * by)) / (al * bl), -1, 1);
      return Math.acos(cos) * (180 / Math.PI);
    }

    function maxDeviationFromLine(points) {
      if (points.length <= 2) return 0;
      let max = 0;
      for (let i = 1; i < points.length - 1; i += 1) {
        max = Math.max(max, pointLineDistance(points[i], points[0], points[points.length - 1]));
      }
      return max;
    }

    function splitPointsAtCorners(points, closed, angleThreshold) {
      const segments = [];
      if (!points.length) return segments;
      if (!closed) {
        let current = [points[0]];
        for (let i = 1; i < points.length - 1; i += 1) {
          current.push(points[i]);
          const turn = getTurnAngleDegrees(points[i - 1], points[i], points[i + 1]);
          if (turn >= angleThreshold) {
            current.push(points[i + 1]);
            segments.push(current);
            current = [points[i + 1]];
          }
        }
        if (current[current.length - 1] !== points[points.length - 1]) current.push(points[points.length - 1]);
        segments.push(current);
        return segments.filter(seg => seg.length >= 2);
      }

      const corners = [];
      const total = points.length;
      for (let i = 0; i < total; i += 1) {
        const prev = points[(i - 1 + total) % total];
        const current = points[i];
        const next = points[(i + 1) % total];
        const turn = getTurnAngleDegrees(prev, current, next);
        if (turn >= angleThreshold) corners.push(i);
      }
      if (!corners.length) return [points.slice()];
      for (let c = 0; c < corners.length; c += 1) {
        const start = corners[c];
        const end = corners[(c + 1) % corners.length];
        const segment = [points[start]];
        let idx = (start + 1) % total;
        while (idx !== end) {
          segment.push(points[idx]);
          idx = (idx + 1) % total;
        }
        segment.push(points[end]);
        segments.push(segment);
      }
      return segments.filter(seg => seg.length >= 2);
    }

    function smoothSegmentToQuadratics(points, deviationThreshold) {
      if (points.length <= 2) return { smooth: false, points };
      const deviation = maxDeviationFromLine(points);
      if (deviation < deviationThreshold) return { smooth: false, points: [points[0], points[points.length - 1]] };
      return { smooth: true, points };
    }

    function chaikinSmooth(points, iterations = 1) {
      if (points.length < 3) return points.slice();
      let output = points.slice();
      for (let iteration = 0; iteration < iterations; iteration += 1) {
        const next = [output[0]];
        for (let i = 0; i < output.length - 1; i += 1) {
          const a = output[i];
          const b = output[i + 1];
          next.push({ x: (0.75 * a.x) + (0.25 * b.x), y: (0.75 * a.y) + (0.25 * b.y) });
          next.push({ x: (0.25 * a.x) + (0.75 * b.x), y: (0.25 * a.y) + (0.75 * b.y) });
        }
        next.push(output[output.length - 1]);
        output = next;
      }
      return output;
    }

    function catmullRomSegmentToCubic(p0, p1, p2, p3, tension = 0.72) {
      return {
        c1: {
          x: p1.x + ((p2.x - p0.x) * tension / 6),
          y: p1.y + ((p2.y - p0.y) * tension / 6)
        },
        c2: {
          x: p2.x - ((p3.x - p1.x) * tension / 6),
          y: p2.y - ((p3.y - p1.y) * tension / 6)
        },
        end: p2
      };
    }

    function serialiseSegmentsAsPath(segments, closed) {
      if (!segments.length || !segments[0].points.length) return '';
      let d = `M ${segments[0].points[0].x.toFixed(2)} ${segments[0].points[0].y.toFixed(2)}`;
      let currentPoint = segments[0].points[0];

      for (let s = 0; s < segments.length; s += 1) {
        const seg = segments[s];
        let pts = seg.points;
        if (!pts.length) continue;
        const first = pts[0];

        if (Math.abs(first.x - currentPoint.x) > 0.01 || Math.abs(first.y - currentPoint.y) > 0.01) {
          d += ` L ${first.x.toFixed(2)} ${first.y.toFixed(2)}`;
        }

        if (!seg.smooth || pts.length < 3) {
          for (let i = 1; i < pts.length; i += 1) {
            d += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`;
          }
          currentPoint = pts[pts.length - 1];
          continue;
        }

        // Soften only the curved run; endpoints remain fixed to preserve corners.
        pts = chaikinSmooth(pts, 1);
        for (let i = 0; i < pts.length - 1; i += 1) {
          const p0 = pts[Math.max(0, i - 1)];
          const p1 = pts[i];
          const p2 = pts[i + 1];
          const p3 = pts[Math.min(pts.length - 1, i + 2)];
          const cubic = catmullRomSegmentToCubic(p0, p1, p2, p3);
          d += ` C ${cubic.c1.x.toFixed(2)} ${cubic.c1.y.toFixed(2)} ${cubic.c2.x.toFixed(2)} ${cubic.c2.y.toFixed(2)} ${cubic.end.x.toFixed(2)} ${cubic.end.y.toFixed(2)}`;
        }
        currentPoint = pts[pts.length - 1];
      }

      if (closed) d += ' Z';
      return d;
    }

    function simplifyStraightLinePaths(svg, epsilon) {
      svg.querySelectorAll('path').forEach(path => {
        const d = path.getAttribute('d') || '';
        if (!d) return;
        // Only simplify pure line paths. Leave already-curved paths alone.
        if (/[^0-9eE.\-+,\sMLHVZmlhvz]/.test(d)) return;
        const subpaths = parseSimpleLinePath(d);
        if (!subpaths || !subpaths.length) return;
        const rebuilt = subpaths.map(subpath => {
          let points = subpath.points.slice();
          if (subpath.closed && points.length > 2) {
            while (points.length > 1 && Math.abs(points[0].x - points[points.length - 1].x) < 0.001 && Math.abs(points[0].y - points[points.length - 1].y) < 0.001) {
              points.pop();
            }
          }
          const splitSegments = splitPointsAtCorners(points, subpath.closed, 52);
          const processed = splitSegments.map(segment => {
            let working = segment.slice();
            if (working.length > 2) working = simplifyRdp(working, epsilon * 0.92);
            if (working.length < 2) working = [segment[0], segment[segment.length - 1]];
            const smooth = smoothSegmentToQuadratics(working, Math.max(0.65, epsilon * 0.7));
            return { points: smooth.points, smooth: smooth.smooth };
          });
          return serialiseSegmentsAsPath(processed, subpath.closed);
        }).filter(Boolean).join(' ');
        if (rebuilt) path.setAttribute('d', rebuilt);
      });
    }

    function removeTinyForegroundComponents(imageData, minimumArea) {
      if (minimumArea <= 1) return imageData;
      const { width, height, data } = imageData;
      const mask = new Uint8Array(width * height);
      const visited = new Uint8Array(width * height);
      const queue = new Int32Array(width * height);

      for (let i = 0; i < mask.length; i += 1) {
        mask[i] = data[i * 4 + 3] > 0 ? 1 : 0;
      }

      for (let i = 0; i < mask.length; i += 1) {
        if (!mask[i] || visited[i]) continue;
        let head = 0;
        let tail = 0;
        queue[tail++] = i;
        visited[i] = 1;

        while (head < tail) {
          const current = queue[head++];
          const x = current % width;
          const y = Math.floor(current / width);
          for (let yy = Math.max(0, y - 1); yy <= Math.min(height - 1, y + 1); yy += 1) {
            for (let xx = Math.max(0, x - 1); xx <= Math.min(width - 1, x + 1); xx += 1) {
              const next = yy * width + xx;
              if (!visited[next] && mask[next]) {
                visited[next] = 1;
                queue[tail++] = next;
              }
            }
          }
        }

        if (tail < minimumArea) {
          for (let q = 0; q < tail; q += 1) {
            const pixel = queue[q] * 4;
            data[pixel] = 255;
            data[pixel + 1] = 255;
            data[pixel + 2] = 255;
            data[pixel + 3] = 0;
          }
        }
      }
      return imageData;
    }

    function buildBinaryImageData(img) {
      const sourceMax = Math.max(img.naturalWidth, img.naturalHeight);
      const targetMax = clamp(sourceMax, 1000, 1800);
      const scale = targetMax / sourceMax;
      const width = Math.max(1, Math.round(img.naturalWidth * scale));
      const height = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.clearRect(0, 0, width, height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Keep preprocessing deliberately light. Potrace performs the curve
      // optimisation; heavy blur here would damage real corners such as E/T/L.
      const smoothing = Number(els.smoothing.value || 0);
      const blurPx = smoothing <= 1 ? 0 : Math.min(0.65, (smoothing - 1) * 0.09 * Math.max(1, Math.sqrt(scale)));
      ctx.filter = blurPx ? `blur(${blurPx}px)` : 'none';
      ctx.drawImage(img, 0, 0, width, height);
      ctx.filter = 'none';

      const data = ctx.getImageData(0, 0, width, height);
      const threshold = Number(els.threshold.value);
      const invert = els.invert.checked;
      for (let i = 0; i < data.data.length; i += 4) {
        const alpha = data.data[i + 3];
        const luminance = (data.data[i] * 0.2126) + (data.data[i + 1] * 0.7152) + (data.data[i + 2] * 0.0722);
        let foreground = alpha > 15 && luminance < threshold;
        if (invert) foreground = !foreground && alpha > 15;
        data.data[i] = foreground ? 0 : 255;
        data.data[i + 1] = foreground ? 0 : 255;
        data.data[i + 2] = foreground ? 0 : 255;
        data.data[i + 3] = 255;
      }

      const cleanup = Number(els.detail.value || 0);
      const analysisData = new ImageData(new Uint8ClampedArray(data.data), data.width, data.height);
      // Analysis expects transparent background.
      for (let i = 0; i < analysisData.data.length; i += 4) {
        const isBlack = analysisData.data[i] < 128;
        analysisData.data[i + 3] = isBlack ? 255 : 0;
      }
      const minimumArea = cleanup === 0 ? 0 : Math.round(Math.pow(cleanup, 1.45) * Math.max(1, scale * scale) * 0.55);
      removeTinyForegroundComponents(analysisData, minimumArea);

      // Mirror cleaned analysis pixels back into the opaque canvas used by Potrace.
      for (let i = 0; i < data.data.length; i += 4) {
        const foreground = analysisData.data[i + 3] > 0;
        data.data[i] = foreground ? 0 : 255;
        data.data[i + 1] = foreground ? 0 : 255;
        data.data[i + 2] = foreground ? 0 : 255;
        data.data[i + 3] = 255;
      }
      ctx.putImageData(data, 0, 0);
      return { canvas, imageData: analysisData, processingScale: scale };
    }

    function cleanupTracedSvg(svgString, options = {}) {
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;
      svg.querySelectorAll('script,desc,title').forEach(el => el.remove());
      svg.querySelectorAll('path').forEach(path => {
        const fill = (path.getAttribute('fill') || '').replace(/\s/g, '').toLowerCase();
        const opacity = Number(path.getAttribute('fill-opacity') || '1');
        if (opacity === 0 || fill === 'rgb(255,255,255)' || fill === '#fff' || fill === '#ffffff' || fill === 'none') {
          path.remove();
        } else {
          path.setAttribute('fill', '#000000');
          path.removeAttribute('stroke');
        }
      });
      simplifyStraightLinePaths(svg, options.lineEpsilon || 1.5);
      return cropAndNormalise(svg);
    }

    function normaliseUploadedSvg(svgString) {
      const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
      const svg = doc.documentElement;
      const disallowed = svg.querySelector('text,image,foreignObject');
      if (disallowed) throw new Error('V1 accepts outlined vector shapes only. Convert text and embedded images to paths first.');
      svg.querySelectorAll('style,script,metadata,desc,title').forEach(el => el.remove());
      svg.querySelectorAll('path,rect,circle,ellipse,polygon,polyline,line').forEach(shape => {
        shape.setAttribute('fill', '#000000');
        const stroke = shape.getAttribute('stroke');
        if (stroke && stroke !== 'none') shape.setAttribute('stroke', '#000000');
      });
      return cropAndNormalise(svg);
    }

    function cropAndNormalise(sourceSvg) {
      const holder = document.createElement('div');
      holder.style.cssText = 'position:fixed;left:-99999px;top:-99999px;width:1000px;height:1000px;visibility:hidden;';
      const svg = sourceSvg.cloneNode(true);
      svg.setAttribute('xmlns', SVG_NS);
      svg.setAttribute('width', '1000');
      svg.setAttribute('height', '1000');
      holder.appendChild(svg);
      document.body.appendChild(holder);
      const drawable = svg.querySelector('g') || svg;
      let box;
      try { box = drawable.getBBox(); } catch (_) { box = null; }
      holder.remove();
      if (!box || !box.width || !box.height) throw new Error('No usable filled vector shapes were found.');

      const pad = Math.max(box.width, box.height) * 0.002;
      const x = box.x - pad;
      const y = box.y - pad;
      const width = box.width + pad * 2;
      const height = box.height + pad * 2;
      svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      const output = new XMLSerializer().serializeToString(svg);
      return { output, aspect: width / height };
    }

    function canvasToPngBlob(canvas) {
      return new Promise((resolve, reject) => {
        if (!canvas || typeof canvas.toBlob !== 'function') {
          reject(new Error('This browser cannot prepare the PNG for tracing.'));
          return;
        }
        canvas.toBlob(blob => {
          if (blob) resolve(blob);
          else reject(new Error('The PNG could not be prepared for tracing.'));
        }, 'image/png');
      });
    }

    function traceWithImageTracer(imageData, smoothingLevel, detailLevel) {
      if (!window.ImageTracer) throw new Error('The fallback raster tracer did not load.');
      const options = {
        colorsampling: 0,
        numberofcolors: 2,
        colorquantcycles: 1,
        pathomit: Math.max(1, Math.round(Math.pow(detailLevel, 1.2) * 0.7)),
        ltres: 1.7 + (smoothingLevel * 0.32),
        qtres: 1.15 + (smoothingLevel * 0.22),
        rightangleenhance: true,
        blurradius: 0,
        blurdelta: 20,
        strokewidth: 0,
        linefilter: true,
        roundcoords: 2,
        viewbox: true,
        desc: false,
        pal: [
          { r: 0, g: 0, b: 0, a: 255 },
          { r: 255, g: 255, b: 255, a: 0 }
        ]
      };
      return window.ImageTracer.imagedataToSVG(imageData, options);
    }

    function traceWithClassicPotrace(canvas, smoothingLevel, detailLevel) {
      return new Promise((resolve, reject) => {
        const potrace = window.Potrace;
        if (!potrace || typeof potrace.loadImageFromUrl !== 'function' || typeof potrace.process !== 'function') {
          reject(new Error('Classic Potrace did not load.'));
          return;
        }
        try {
          potrace.setParameter({
            turnpolicy: 'minority',
            turdsize: Math.max(1, Math.round(Math.pow(detailLevel, 1.25) * 0.35)),
            optcurve: true,
            alphamax: 0.92 + (smoothingLevel * 0.025),
            opttolerance: 0.18 + (smoothingLevel * 0.04)
          });
          potrace.loadImageFromUrl(canvas.toDataURL('image/png'));
          potrace.process(() => {
            try {
              const svg = potrace.getSVG(1);
              if (typeof svg !== 'string' || !svg.includes('<svg')) {
                reject(new Error('Classic Potrace returned no SVG.'));
                return;
              }
              resolve(svg);
            } catch (error) {
              reject(error);
            }
          });
        } catch (error) {
          reject(error);
        }
      });
    }

    async function processArtwork() {
      if (!state.file) return;
      setReady(false);
      state.analysis = null;
      state.sizeAnalysis = null;
      updateDownloadAvailability();
      setStatus('Converting artwork…', 'warn');
      if (els.engine) els.engine.textContent = state.sourceType === 'svg' ? 'Uploaded SVG' : 'Checking…';
      if (els.prepare) els.prepare.disabled = true;
      try {
        let result;
        if (state.sourceType === 'svg') {
          result = normaliseUploadedSvg(state.sourceSvg);
          const svgAnalysisData = await rasteriseSvgForAnalysis(result.output);
          state.analysis = analyseBinaryImageData(svgAnalysisData);
        } else {
          const processed = buildBinaryImageData(state.sourceImage);
          state.analysis = analyseBinaryImageData(processed.imageData);
          const smoothingLevel = Number(els.smoothing.value || 0);
          const detailLevel = Number(els.detail.value || 0);

          let svgString = '';
          let engineUsed = 'Classic Potrace';
          try {
            svgString = await traceWithClassicPotrace(processed.canvas, smoothingLevel, detailLevel);
          } catch (potraceError) {
            console.warn('[CCS Cut Maker] Classic Potrace failed; using fallback tracer:', potraceError);
            engineUsed = 'ImageTracer fallback';
            svgString = traceWithImageTracer(processed.imageData, smoothingLevel, detailLevel);
          }
          if (typeof svgString !== 'string' || !svgString.includes('<svg')) {
            throw new Error('Neither raster tracing engine produced a usable vector path.');
          }
          result = cleanupTracedSvg(svgString, { lineEpsilon: engineUsed === 'Classic Potrace' ? 1.3 : 1.15 });
          result.engineUsed = engineUsed;
        }
        state.outputSvg = result.output;
        state.aspect = result.aspect;
        if (els.engine) els.engine.textContent = state.sourceType === 'svg' ? 'Uploaded SVG' : (result.engineUsed || 'Unknown');
        if (els.viewConverted) els.viewConverted.disabled = false;
        renderPreview();
        updateDimensions();
        setStatus('Preview generated. Check the shape, zoom in if needed and confirm the dimensions.', 'ready');
      } catch (error) {
        state.outputSvg = '';
        state.analysis = null;
        if (els.engine) els.engine.textContent = 'Failed';
        els.preview.innerHTML = '<div class="ccs-cut-maker__empty">The artwork could not be converted.</div>';
        setStatus(error.message || 'The artwork could not be converted.', 'error');
        updateComplexityUi();
      }
      validate();
    }

    const debouncedProcess = debounce(processArtwork, 220);

    function buildFinalSvg() {
      const width = Math.round(state.widthMm * 10) / 10;
      const height = Math.round(state.heightMm * 10) / 10;
      const coloured = colouriseSvg(state.outputSvg);
      const doc = new DOMParser().parseFromString(coloured, 'image/svg+xml');
      if (doc.querySelector('parsererror')) {
        return coloured.replace(/<svg\b/, `<svg width="${width}mm" height="${height}mm"`);
      }

      const svg = doc.documentElement;
      svg.setAttribute('width', `${width}mm`);
      svg.setAttribute('height', `${height}mm`);
      svg.setAttribute('data-converted-by', 'CustomCarStickers.co.uk');

      svg.querySelectorAll('[data-ccs-converter-credit], metadata[data-ccs-converter-credit]').forEach(node => node.remove());

      const title = doc.createElementNS(SVG_NS, 'title');
      title.setAttribute('data-ccs-converter-credit', 'true');
      title.textContent = 'Artwork Converted by CustomCarStickers.co.uk';

      const description = doc.createElementNS(SVG_NS, 'desc');
      description.setAttribute('data-ccs-converter-credit', 'true');
      description.textContent = 'Artwork Converted by CustomCarStickers.co.uk using the free online SVG vector converter.';

      const metadata = doc.createElementNS(SVG_NS, 'metadata');
      metadata.setAttribute('data-ccs-converter-credit', 'true');
      metadata.textContent = 'Artwork Converted by CustomCarStickers.co.uk';

      svg.insertBefore(metadata, svg.firstChild);
      svg.insertBefore(description, svg.firstChild);
      svg.insertBefore(title, svg.firstChild);
      svg.insertBefore(doc.createComment(' Artwork Converted by CustomCarStickers.co.uk '), svg.firstChild);

      return new XMLSerializer().serializeToString(svg);
    }

    function triggerDownload() {
      if (!state.outputSvg || !state.widthMm || !state.heightMm) return;
      const width = Math.round(state.widthMm * 10) / 10;
      const height = Math.round(state.heightMm * 10) / 10;
      const safeBase = (state.file.name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || 'artwork').slice(0, 70);
      const blob = new Blob([buildFinalSvg()], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeBase}-${width}x${height}mm-cut.svg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    }

    els.upload.addEventListener('change', async () => {
      const file = els.upload.files?.[0];
      state.file = file || null;
      state.outputSvg = '';
      state.viewMode = 'converted';
      setReady(false);
      updateDownloadAvailability();
      if (!file) {
        els.fileName.textContent = '—';
        if (els.engine) els.engine.textContent = '—';
        updateComplexityUi();
        validate();
        return;
      }
      els.fileName.textContent = file.name;
      if (state.originalUrl) URL.revokeObjectURL(state.originalUrl);
      state.originalUrl = URL.createObjectURL(file);
      if (els.viewOriginal) els.viewOriginal.disabled = false;
      if (els.viewConverted) els.viewConverted.disabled = true;
      if (state.hidden.ccsOriginalName) state.hidden.ccsOriginalName.value = file.name;
      try {
        const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
        state.sourceType = isSvg ? 'svg' : 'raster';
        els.rasterControls.classList.toggle('ccs-cut-maker__hidden', isSvg);
        if (isSvg) state.sourceSvg = await loadSvgFile(file);
        else state.sourceImage = await loadImageFile(file);
        await processArtwork();
      } catch (error) {
        setStatus(error.message || 'The uploaded file could not be read.', 'error');
      }
    });

    els.threshold.addEventListener('input', () => {
      els.thresholdValue.textContent = els.threshold.value;
      debouncedProcess();
    });
    els.smoothing.addEventListener('input', () => {
      els.smoothingValue.textContent = els.smoothing.value;
      debouncedProcess();
    });
    els.detail.addEventListener('input', () => {
      els.detailValue.textContent = els.detail.value;
      debouncedProcess();
    });
    els.invert.addEventListener('change', debouncedProcess);
    if (els.vectorColour) {
      els.vectorColour.addEventListener('input', () => {
        state.vectorColour = normaliseHexColour(els.vectorColour.value);
        if (els.vectorColourValue) els.vectorColourValue.textContent = state.vectorColour;
        if (state.hidden.ccsVectorColour) state.hidden.ccsVectorColour.value = state.vectorColour;
        if (state.viewMode === 'converted') renderPreview();
        setReady(false);
        validate();
      });
    }
    [els.sizeBy, els.size].forEach(el => el.addEventListener('input', updateDimensions));
    if (els.approval) els.approval.addEventListener('change', validate);
    els.download.addEventListener('click', triggerDownload);

    els.zoom.addEventListener('input', () => setZoom(els.zoom.value));
    els.zoomIn.addEventListener('click', () => setZoom(state.zoom + 0.25));
    els.zoomOut.addEventListener('click', () => setZoom(state.zoom - 0.25));
    if (els.zoomReset) els.zoomReset.addEventListener('click', () => setZoom(1));
    if (els.fit) els.fit.addEventListener('click', () => setZoom(1));
    if (els.viewOriginal) els.viewOriginal.addEventListener('click', () => setViewMode('original'));
    if (els.viewConverted) els.viewConverted.addEventListener('click', () => setViewMode('converted'));
    if (els.applyRecommended) els.applyRecommended.addEventListener('click', () => {
      if (!state.sizeAnalysis) return;
      els.sizeBy.value = 'width';
      els.size.value = state.sizeAnalysis.recommendedWidth;
      updateDimensions();
    });

    if (els.prepare) els.prepare.addEventListener('click', () => {
      if (!validate() || !ensureOrderFields()) {
        if (!state.form) setStatus('The product add-to-cart form could not be found.', 'error');
        return;
      }
      const width = Math.round(state.widthMm * 10) / 10;
      const height = Math.round(state.heightMm * 10) / 10;
      const safeBase = (state.file.name.replace(/\.[^.]+$/, '').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || 'artwork').slice(0, 70);
      const generatedFile = new File([buildFinalSvg()], `${safeBase}-${width}x${height}mm-cut.svg`, { type: 'image/svg+xml' });
      const transfer = new DataTransfer();
      transfer.items.add(generatedFile);
      state.hidden.ccsCutFile.files = transfer.files;
      state.hidden.ccsDimensions.value = `${width} × ${height} mm`;
      state.hidden.ccsSizeBy.value = els.sizeBy.value;
      state.hidden.ccsOriginalName.value = state.file.name;
      state.hidden.ccsTraceSettings.value = state.sourceType === 'svg'
        ? 'Uploaded SVG sanitised and cropped'
        : `Raster trace; threshold ${els.threshold.value}; smoothing ${els.smoothing.value}; cleanup ${els.detail.value}; invert ${els.invert.checked ? 'yes' : 'no'}`;
      state.hidden.ccsApproval.value = els.approval?.checked ? 'Confirmed' : 'Not requested';
      state.hidden.ccsComplexity.value = state.sizeAnalysis
        ? `${state.sizeAnalysis.label}; score ${state.sizeAnalysis.score}/100; min feature ${state.sizeAnalysis.minFeatureMm.toFixed(2)} mm; shapes ${state.sizeAnalysis.components}`
        : 'Not calculated';
      setReady(true);
      validate();
    });

    ensureOrderFields();
    if (root.dataset.blockSubmit === 'true') {
      document.addEventListener('submit', event => {
        if (event.target !== state.form) return;
        if (!state.ready) {
          event.preventDefault();
          event.stopImmediatePropagation();
          setStatus('Prepare and approve the cut file before adding this product to the basket.', 'error');
          root.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, true);
    }

    window.addEventListener('resize', debounce(updatePreviewCanvasSize, 120));
    state.vectorColour = normaliseHexColour(els.vectorColour?.value || '#000000');
    if (els.vectorColourValue) els.vectorColourValue.textContent = state.vectorColour;
    setZoom(1);
    updateComplexityUi();
    updateDownloadAvailability();
    validate();
  }

  function boot() {
    document.querySelectorAll('[data-ccs-cut-maker]').forEach(init);
  }
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();
